import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import { task } from 'ember-concurrency';
import perform from 'ember-concurrency/helpers/perform';
import { t } from 'ember-intl';
import PowerSelect from 'ember-power-select/components/power-select';
import { trackedFunction } from 'reactiveweb/function';

import Button from 'umg-ember-backend/components/ui/button';
import ContentWithSidebar from 'umg-ember-backend/components/ui/content-with-sidebar';
import { query } from 'umg-ember-backend/data/builders/umg-rest';
import { imageUrl } from 'umg-ember-backend/helpers/image-url';

import imageCropper from './image-cropper-modifier';

const FORMAT_MAP = {
  Image: ['989', '990', '991', '992', '998', '993', '994', '995', '996'],
  Video: ['1000', '1001'],
};

export default class AssetToolsCropComponent extends Component {
  // Services
  @service flashMessages;
  @service store;

  // Defaults
  @tracked selectedFormat = null;

  // Getter and setter
  get aspectRatio() {
    return this.selectedFormat.get('width') / this.selectedFormat.get('height');
  }

  get assetFormatRelation() {
    if (!this.selectedFormat) {
      return null;
    }

    const { asset } = this.args;
    const assetFormatRelations = asset.resolvedAssetFormatRelations.value || [];

    return assetFormatRelations.find((relation) => {
      return relation.assetFormat.id === this.selectedFormat.id;
    });
  }

  get cropData() {
    let relation = this.assetFormatRelation;
    let data = {};

    if (relation?.get('cropDimension')) {
      let [width, height] = relation.get('cropDimension').split('|');
      data.width = parseInt(width);
      data.height = parseInt(height);
    }

    if (relation?.get('cropXy')) {
      let [x, y] = relation.get('cropXy').split('|');
      data.x = parseInt(x);
      data.y = parseInt(y);
    }

    return data;
  }

  // Functions
  selectFormat = (format) => {
    this.selectedFormat = format;
  };

  setCropData = (event) => {
    this.selection = event.detail;
  };

  loadFormats = trackedFunction(this, async () => {
    // Avoid an infinity loop https://github.com/NullVoxPopuli/ember-resources/issues/340
    await Promise.resolve();
    let assetTypeSelector = this.args.asset.assetTypeSelector;

    const { content } = await this.store.request(
      query('asset-format', {
        'filter[id]': FORMAT_MAP[assetTypeSelector],
      }),
    );

    this.selectedFormat = content.data[0];

    return content.data;
  });

  saveAssetFormatRelation = task({ restartable: true }, async () => {
    try {
      let cropData = this.selection;
      let relation = this.assetFormatRelation;

      if (!relation) {
        relation = this.store.createRecord('asset-format-relation', {
          asset: this.args.asset,
          assetFormat: this.selectedFormat,
          sourceFormatId: this.args.asset.isImage ? '195' : '348',
        });
      }

      relation.setProperties({
        cropDimension: `${cropData.width}|${cropData.height}`,
        cropXy: `${cropData.x}|${cropData.y}`,
        flagRedo: '1',
        dateStart: null,
        dateEnd: null,
        width: this.selectedFormat.width,
        height: this.selectedFormat.height,
      });

      await relation.save();

      this.flashMessages.success('flash.asset_crop.success');
    } catch (error) {
      this.flashMessages.serverError(error);
    }
  });

  // Template
  <template>
    <ContentWithSidebar @sidebarPosition='left' @hasSpacing={{true}}>
      <:sidebar>
        <label>
          {{t 'asset_tools.crop.format'}}
        </label>
        <PowerSelect
          @onChange={{this.selectFormat}}
          @options={{this.loadFormats.value}}
          @searchEnabled={{false}}
          @selected={{this.selectedFormat}}
          as |option|
        >
          {{option.croppingFormatLabel}}
        </PowerSelect>
        <br />
        {{#if this.selectedFormat}}
          <Button
            @label={{t 'asset_tools.crop.crop_image'}}
            {{on 'click' (perform this.saveAssetFormatRelation)}}
          />
        {{/if}}
      </:sidebar>
      <:content>
        {{#if this.selectedFormat}}
          <label data-test-cropping-format-label>
            {{this.selectedFormat.croppingFormatLabel}}
          </label>
          <div data-test-cropping-container>
            <img
              src={{imageUrl (array @asset)}}
              alt={{@asset.headline}}
              {{imageCropper
                aspectRatio=this.aspectRatio
                onCrop=this.setCropData
                data=this.cropData
              }}
            />
          </div>
        {{/if}}
      </:content>
    </ContentWithSidebar>
  </template>
}
